import { Link } from 'react-router-dom'
import { Card, Badge, Spinner } from '@/components/ui'
import { useAccounts } from '@/context/AccountContext'
import { getPlatformDisplayName } from '@/services/platforms'

function VoiceBadge({ mode }: { mode: string }) {
  switch (mode) {
    case 'automated':
      return <Badge>Voice Active</Badge>
    case 'manual':
      return <Badge variant="outline">Manual Voice</Badge>
    default:
      return <Badge variant="outline">Voice Off</Badge>
  }
}

export function AccountSelector() {
  const { accounts, selectedAccount, selectAccount, isLoading } = useAccounts()

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium">Post As</h2>
        {selectedAccount && (
          <Link to="/accounts" className="text-xs text-secondary-text hover:text-primary-text">
            Manage voice &rarr;
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <span className="text-secondary-text">Loading accounts...</span>
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-secondary-text mb-3">No accounts connected</p>
          <Link
            to="/accounts"
            className="inline-block px-4 py-2 bg-primary-text text-primary-bg rounded text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Connect Account
          </Link>
        </div>
      ) : accounts.length === 1 ? (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            {accounts[0].profile_image_url && (
              <img
                src={accounts[0].profile_image_url}
                alt={accounts[0].display_name || accounts[0].username}
                className="w-8 h-8 rounded-full"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {accounts[0].display_name || `@${accounts[0].username}`}
              </p>
              <p className="text-xs text-secondary-text">@{accounts[0].username}</p>
            </div>
            <Badge variant="outline">{getPlatformDisplayName(accounts[0].platform)}</Badge>
            <VoiceBadge mode={accounts[0].voice_config.mode} />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((account) => (
            <button
              key={account.id}
              onClick={() => selectAccount(account.id)}
              className={`w-full flex items-center gap-3 p-2 rounded transition-colors ${
                selectedAccount?.id === account.id
                  ? 'bg-secondary-bg ring-1 ring-border'
                  : 'hover:bg-secondary-bg'
              }`}
            >
              {account.profile_image_url && (
                <img
                  src={account.profile_image_url}
                  alt={account.display_name || account.username}
                  className="w-8 h-8 rounded-full"
                />
              )}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium truncate">
                  {account.display_name || `@${account.username}`}
                </p>
                <p className="text-xs text-secondary-text">@{account.username}</p>
              </div>
              <Badge variant="outline">{getPlatformDisplayName(account.platform)}</Badge>
              <VoiceBadge mode={account.voice_config.mode} />
            </button>
          ))}
        </div>
      )}
    </Card>
  )
}
